import React, { Component } from "react";
import "./CustomerDashboard.css";
import Loader from "../Loader";
import InquiryBanner from "./InquiryBanner";

const API_BASE = "https://posible-crm-engine-1066595118037.asia-south1.run.app/api/customer-intelligence";

const ArrowUpIcon = ({ className = "w-4 h-4" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
  </svg>
);

const ArrowDownIcon = ({ className = "w-4 h-4" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.5}
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

// Maps a segment's display name to the key used inside the `summary` API
// response, so we can pull its change_percent / trend_label for the
// increase/decrease indicator that now lives in the segment list row.
const SEGMENT_SUMMARY_KEY_MAP = {
  Champions: "champions",
  "Loyal Customers": "loyalCustomers",
  "Potential Loyalist": "potentialLoyalists",
  "New Promising": "newPromising",
  "At Risk": "atRisk",
  Lost: "lost",
};

// Converts a raw "days since last visit" number into a friendlier label.
// Under 30 days -> "X days ago". 30+ days -> "N+ months ago"
// e.g. 94 days -> Math.floor(94/30) = 3 -> "3+ months ago"
const formatRecency = (days) => {
  if (days === undefined || days === null || isNaN(days)) return "";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return `${months}+ months ago`;
};

// The /summary API's reportDate is set once per day, when the backend's
// scheduled job (re)computes this report — not on every page load — so
// "today at 4:02 PM" can mean "as of this morning's run", not "just now".
const formatLastUpdated = (reportDateMs) => {
  if (!reportDateMs) return "";
  const reportDate = new Date(Number(reportDateMs));
  const now = new Date();
  const time = reportDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  if (reportDate.toDateString() === now.toDateString()) return `today at ${time}`;

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (reportDate.toDateString() === yesterday.toDateString()) return `yesterday at ${time}`;

  return `${reportDate.toLocaleDateString([], { day: "numeric", month: "short" })} at ${time}`;
};

// The backend's full lookback window (ANALYSIS_PERIOD_DAYS in
// CustomerInsights.js) is read from the /summary API's `analysis_days` at
// render time instead of being duplicated here as a constant — a hardcoded
// copy is exactly what went stale the last time the backend window changed
// (180 -> 365 days). This default is only used before that call returns.
const DEFAULT_ANALYSIS_PERIOD_DAYS = 365;
const RECENT_WINDOW_DAYS = 60;
const RECENT_WINDOW_MONTHS = RECENT_WINDOW_DAYS / 30; // 2

// Champions / Loyal Customers / Potential Loyalist are classified in the
// backend's getCustomersSegmentName() off `frequencyLast60Days` (visits in
// the trailing 60 days), NOT the full-window total. So their reported
// buying cycle has to be built from that same 60-day figure, or the cadence
// shown won't match the number that actually earned them the label. At
// Risk, Lost, and New Promising are classified off recency / first-visit,
// so the full-window total (analysisPeriodMonths, passed in from the
// /summary response) is the correct one for those.
const RECENT_WINDOW_SEGMENTS = ["Champions", "Loyal Customers", "Potential Loyalist"];

// ASSUMPTION: the /segments API exposes the 60-day figure as
// `avgFrequencyLast60Days` (camelCase of the backend's
// avg_frequency_last_60_days), following the same naming convention as
// avgFrequencyRaw / avgRecencyDays / avgAOV. Double check this against the
// actual API response and rename below if it differs.
const formatBuyingCycle = (segmentName, avgFrequencyRaw, avgFrequencyLast60Days, analysisPeriodMonths) => {
  const usesRecentWindow = RECENT_WINDOW_SEGMENTS.includes(segmentName);
  const raw = Number(usesRecentWindow ? avgFrequencyLast60Days : avgFrequencyRaw) || 0;
  const windowMonths = usesRecentWindow ? RECENT_WINDOW_MONTHS : analysisPeriodMonths;

  if (raw <= 0) return "—";

  const visitsPerMonth = raw / windowMonths;

  if (visitsPerMonth >= 1) {
    // e.g. 3.5 visits every month
    const rounded = Math.round(visitsPerMonth * 10) / 10;
    return `${rounded}/month`;
  }

  // Less than one visit a month -> express as "once every N months"
  const monthsPerVisit = Math.max(Math.round(1 / visitsPerMonth), 1);
  return monthsPerVisit === 1 ? "Once a month" : `Once every ${monthsPerVisit} months`;
};

// Same markup/classes the old summary boxes used for their growth indicator
// (summary-card-growth / growth-icon / growth-label), just reused inline in
// the segment list row instead of inside a SummaryCard.
const TrendIndicator = ({ percent, label }) => {
  const value = Number(percent) || 0;
  const isPositive = value > 0;
  const isNegative = value < 0;

  return (
    <div className={`summary-card-growth ${isPositive ? 'summary-card-growth-positive' : isNegative ? 'summary-card-growth-negative' : 'summary-card-growth-neutral'}`}>
      {isPositive && <ArrowUpIcon className="growth-icon" />}
      {isNegative && <ArrowDownIcon className="growth-icon" />}
      {label && label !== "" && `${Math.abs(value)}%`}
      <span className="growth-label">{label}</span>
    </div>
  );
};

const CustomerCaptureCard = ({ data }) => {
  if (!data) return null;

  const overall_rate = Object.prototype.hasOwnProperty.call(data, 'overall_rate') ? data.overall_rate : 0;
  const anonymous_bills = Object.prototype.hasOwnProperty.call(data, 'anonymous_bills') ? data.anonymous_bills : 0;
  const health_status = Object.prototype.hasOwnProperty.call(data, 'health_status') ? data.health_status : "Poor";

  const isPoor = health_status === "Poor" || health_status === "Critical";
  const isFair = health_status === "Fair";
  const isGood = health_status === "Good";

  const badgeColor = isGood
    ? "capture-badge-good"
    : isFair
      ? "capture-badge-fair"
      : "capture-badge-poor";

  const progressColor = isGood
    ? "capture-progress-good"
    : isFair
      ? "capture-progress-fair"
      : "capture-progress-poor";

  return (
    <div className="kpi-card">
      <div>
        <div className="kpi-card-top">
          <span className="kpi-card-title">Capture Rate</span>
          <span className={`kpi-card-pill ${badgeColor}`}>{health_status}</span>
        </div>
        <div className="kpi-card-value-row">
          <span className="kpi-card-value">{Number(overall_rate).toFixed(0)}%</span>
        </div>
        <div className="kpi-card-progress-bg">
          <div
            className={`kpi-card-progress-bar ${progressColor}`}
            style={{ width: `${overall_rate}%` }}
          />
        </div>
      </div>
      <p className="kpi-card-footer">
        <span><strong>{anonymous_bills.toLocaleString()}</strong> bills anonymous</span>
        {isPoor && <span style={{ color: "#dc2626" }}>Can't re-target {100 - overall_rate}%</span>}
      </p>
    </div>
  );
};

const WalletLiabilityCard = ({ total = 0, customerCount = 0 }) => {
  if (!Number(total)) return null;

  return (
    <div className="kpi-card">
      <div>
        <div className="kpi-card-top">
          <span className="kpi-card-title">Wallet Liability</span>
        </div>
        <div className="kpi-card-value-row">
          <span className="kpi-card-value">₹{Number(total).toLocaleString("en-IN")}</span>
        </div>
      </div>
      <p className="kpi-card-footer">
        <span>Held across <strong>{customerCount}</strong> customers</span>
      </p>
    </div>
  );
};

// membershipGaps is populated from the API's `totalMembershipCount`, which
// is the per-segment count of customers who DO have a Membership
// (exists + non-empty), so summing it gives total active memberships.
// The enrollment % badge is derived straight from activeCount/totalCustomers
// — both already on screen elsewhere, not a new metric.
const ActiveMembershipCard = ({
  activeCount = 0,
  totalCustomers = 0,
}) => {
  if (!Number(activeCount)) return null;

  const percentage = totalCustomers > 0 ? ((activeCount / totalCustomers) * 100).toFixed(1) : null;

  return (
    <div className="kpi-card">
      <div>
        <div className="kpi-card-top">
          <span className="kpi-card-title">Active Members</span>
          {percentage !== null && <span className="kpi-card-pill">{percentage}%</span>}
        </div>
        <div className="kpi-card-value-row">
          <span className="kpi-card-value">{Number(activeCount).toLocaleString("en-IN")}</span>
          <span className="kpi-card-unit">enrolled</span>
        </div>
      </div>
    </div>
  );
};

// avgSpend is the segment-weighted average AOV (sum(avgAOV * customerCount)
// / total customers), computed in render() from the same /segments data the
// table below already shows — not a separately fetched or fabricated figure.
const AverageSpendCard = ({ avgSpend = 0 }) => {
  return (
    <div className="kpi-card">
      <div>
        <div className="kpi-card-top">
          <span className="kpi-card-title">Avg Spend (AOV)</span>
        </div>
        <div className="kpi-card-value-row">
          <span className="kpi-card-value">₹{Number(avgSpend).toLocaleString("en-IN")}</span>
          <span className="kpi-card-unit">/ ticket</span>
        </div>
      </div>
      <p className="kpi-card-footer">Avg bill across all visits</p>
    </div>
  );
};

// `trend` (optional) is the Total Customers figure's own change_count/percent
// — derived in render() by summing every segment's change_count (an
// additive absolute delta), not a separately computed metric.
const SummaryCard = ({ title, value = 0, periodLabel, trend }) => {
  const percent = trend ? Number(trend.percent) || 0 : 0;
  const hasTrend = !!(trend && trend.label);
  const isUp = percent > 0;
  const isDown = percent < 0;

  return (
    <div className="kpi-card">
      <div>
        <div className="kpi-card-top">
          <span className="kpi-card-title">{title}</span>
          {periodLabel && <span className="kpi-card-pill">{periodLabel}</span>}
        </div>
        <div className="kpi-card-value-row">
          <span className="kpi-card-value">{Number(value).toLocaleString("en-IN")}</span>
          {hasTrend && (
            <span className={`kpi-card-trend ${isUp ? "kpi-card-trend-up" : isDown ? "kpi-card-trend-down" : "kpi-card-trend-flat"}`}>
              {isUp && <ArrowUpIcon className="kpi-card-trend-icon" />}
              {isDown && <ArrowDownIcon className="kpi-card-trend-icon" />}
              {Math.abs(percent)}%
            </span>
          )}
        </div>
      </div>
      {hasTrend && <p className="kpi-card-footer">{trend.label}</p>}
    </div>
  );
};

// One static suggested next step per segment, shown as a non-interactive
// chip in the Insights column — there's no campaign-sending feature behind
// it yet, so it's descriptive, not a button.
const SEGMENT_ACTIONS = {
  "Champions": { label: "Perk Invite", className: "action-btn-champions" },
  "Loyal Customers": { label: "Upsell / Referral", className: "action-btn-loyal" },
  "Potential Loyalist": { label: "Nudge Offer", className: "action-btn-potential" },
  "New Promising": { label: "Welcome Journey", className: "action-btn-new-promising" },
  "At Risk": { label: "Win-Back Promo", className: "action-btn-at-risk" },
  "Lost": { label: "Re-engage Blast", className: "action-btn-lost" },
};

// Backend already computes this banding (avg_recency_days < 30 good / < 90
// medium / else low — see getSegmentPerformance), so the pill just reads it
// instead of re-deriving its own threshold on the frontend.
const RECENCY_HEALTH_CLASS = {
  good: "recency-pill-good",
  medium: "recency-pill-medium",
  low: "recency-pill-low",
};

const SegmentRow = ({
  name,
  avgAOV,
  avgRecency,
  avgFrequencyRaw,
  avgFrequencyLast60Days,
  analysisPeriodMonths,
  health,
  customerCount,
  loyaltyGap,
  trend,
  onClick,
  onClickLoyalty,
}) => {
  const showLoyaltyGapsForSegment = [
    "Champions",
    "Loyal Customers",
    "Potential Loyalist",
    "New Promising",
  ];

  const getDotColor = (name) => {
    if (name === "Champions") return "segment-dot-green";
    if (name === "Loyal Customers") return "segment-dot-blue";
    if (name === "At Risk") return "segment-dot-red-blink";
    if (name === "Potential Loyalist") return "segment-dot-purple";
    if (name === "New Promising") return "segment-dot-yellow";
    return "segment-dot-gray";
  };

  const getLabel = (name) => {
    if (name === "Champions") return "Frequent Buyers";
    if (name === "Loyal Customers") return "Consistent Buyers";
    if (name === "At Risk") return "Needs Attention";
    if (name === "Potential Loyalist") return "Growth Opportunity";
    if (name === "New Promising") return "Fresh First-Timers";
    return "";
  };

  const noBuyingCyclesForSegments = ["New Promising"];
  const action = SEGMENT_ACTIONS[name];
  const recencyPillClass = RECENCY_HEALTH_CLASS[health] || "recency-pill-medium";

  return (
    <div className="segment-row" >
      <div >
        <div className="segment-name-wrapper" >
          <div className={`segment-dot ${getDotColor(name)}`}></div>
          <span className="segment-name">{name}</span>
        </div>
          <div className={`segment-label ${name === "At Risk" ? 'segment-label-at-risk' : 'segment-label-default'}`}>
          {getLabel(name)}
        </div>
        {trend && (
          <div className="segment-trend">
            <TrendIndicator percent={trend.percent} label={trend.label} />
          </div>
        )}
      
        <span onClick={onClick} className="segment-view-link">view</span>
      </div>
      <div className="segment-customers">{customerCount !== undefined && customerCount !== null ? customerCount : 0}</div>
      <div >
        <div className="segment-aov">₹{avgAOV}</div>
      </div>
      {!noBuyingCyclesForSegments.includes(name) ? (
        <div className="segment-frequency-number">
          <span className="segment-cycle-chip">
            {formatBuyingCycle(name, avgFrequencyRaw, avgFrequencyLast60Days, analysisPeriodMonths)}
          </span>
        </div>
      ) : (
        <div></div>
      )}
      <div>
        <span className={`segment-recency-pill ${recencyPillClass}`}>
          {formatRecency(avgRecency)}
        </span>
      </div>
      <div className="segment-insights" >
        {action && (
          <span className={`segment-action-chip ${action.className}`}>
            {action.label}
          </span>
        )}
        {loyaltyGap && showLoyaltyGapsForSegment.includes(name) && (
          <div onClick={onClickLoyalty} className="segment-loyalty-gap">
            <div className={`segment-loyalty-box ${loyaltyGap && loyaltyGap.percent ? (loyaltyGap.percent <= 33 ? 'segment-loyalty-box-low' : loyaltyGap.percent <= 66 ? 'segment-loyalty-box-medium' : 'segment-loyalty-box-high') : 'segment-loyalty-box-low'}`}>
              <span className="segment-loyalty-count">{loyaltyGap ? loyaltyGap.count : 0} have Empty Wallet</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Each plan name gets a stable color by its position in the sorted list
// returned from the backend (largest plan first — see getMembershipBreakdown).
const MEMBERSHIP_PLAN_COLORS = ["#7c3aed", "#f59e0b", "#64748b", "#0ea5e9", "#16a34a", "#dc2626"];

// plans come from Membership.Name.keyword — whatever free-text name each
// business gave its own plan, not a fixed tier system (see the comment on
// getMembershipBreakdown in CustomerInsightsApi.js). totalCustomers is the
// same figure already shown in the Total Customers KPI card, reused here to
// derive the non-member count rather than fetched twice.
const MembershipBreakdownCard = ({ plans, totalCustomers }) => {
  if (!plans || plans.length === 0) return null;

  const totalMembers = plans.reduce((sum, p) => sum + p.count, 0);
  const maxCount = plans.reduce((max, p) => Math.max(max, p.count), 0) || 1;
  const nonMemberCount = Math.max(totalCustomers - totalMembers, 0);

  return (
    <div className="dashboard-card breakdown-card">
      <div className="breakdown-header">
        <h3 className="breakdown-title">Membership Breakdown</h3>
        <span className="breakdown-subtitle">{totalMembers} Members</span>
      </div>
      <div className="breakdown-list">
        {plans.map((p, i) => (
          <div className="breakdown-row" key={p.name}>
            <div className="breakdown-row-label">
              <span
                className="breakdown-dot"
                style={{ background: MEMBERSHIP_PLAN_COLORS[i % MEMBERSHIP_PLAN_COLORS.length] }}
              ></span>
              <span className="breakdown-name">{p.name}</span>
            </div>
            <div className="breakdown-row-value">
              <span className="breakdown-count">{p.count}</span>
              <div className="breakdown-bar-bg">
                <div
                  className="breakdown-bar"
                  style={{
                    width: `${(p.count / maxCount) * 100}%`,
                    background: MEMBERSHIP_PLAN_COLORS[i % MEMBERSHIP_PLAN_COLORS.length],
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="breakdown-footer-row">
        <span>Non-Members</span>
        <span>{nonMemberCount} customers</span>
      </div>
    </div>
  );
};

// Purely informational — no "Send Voucher" / "WhatsApp Wish" style action
// buttons here, unlike the segment table's suggestion chips. Those are
// generic campaign ideas; these would imply actually messaging a specific
// real customer, which this app has no wired-up way to do from here.
const MilestoneCard = ({ milestone }) => {
  const isBirthday = milestone.type === "birthday";
  const dueLabel =
    milestone.daysUntil === 0 ? "Today"
      : milestone.daysUntil === 1 ? "Tomorrow"
        : `In ${milestone.daysUntil} days`;

  return (
    <div className={`milestone-card ${isBirthday ? "milestone-birthday" : "milestone-anniversary"}`}>
      <div className="milestone-top">
        <span className="milestone-type-badge">
          {isBirthday ? "🎂 Birthday" : "💍 Anniversary"} · {dueLabel}
        </span>
      </div>
      <h4 className="milestone-name">{milestone.name}</h4>
      {milestone.phone && <p className="milestone-phone">{milestone.phone}</p>}
    </div>
  );
};

const MilestonesFeed = ({ milestones }) => {
  return (
    <div className="milestones-panel">
      <div className="milestones-header">
        <h3 className="milestones-title">Upcoming Birthdays &amp; Anniversaries</h3>
        <span className="milestones-count">Next 30 days</span>
      </div>
      {milestones.length === 0 ? (
        <div className="milestones-empty">
          <span className="milestones-empty-icon" aria-hidden="true">🎂</span>
          <p className="milestones-empty-title">No birthdays or anniversaries in the next 30 days</p>
          <p className="milestones-empty-hint">Add a date of birth or anniversary on the customer's profile to see them here.</p>
        </div>
      ) : (
        <div className="milestones-grid">
          {milestones.map((m, i) => (
            <MilestoneCard key={`${m.customerId}-${m.type}-${i}`} milestone={m} />
          ))}
        </div>
      )}
    </div>
  );
};

// activeValue/sessionsRemaining etc. all come straight from the "packages"
// Elasticsearch index (a different domain the CRM engine reads directly —
// see getPackagesOverview in CustomerInsightsApi.js); nothing here is
// estimated or derived beyond what that endpoint already computed.
const PackagesOverviewCard = ({ data }) => {
  if (!data) return null;
  const { activeCount, activeValue, nearingExpiryCount, expiryWindowDays, expiringSoon } = data;

  return (
    <div className="dashboard-card packages-card">
      <div className="breakdown-header">
        <h3 className="breakdown-title">Packages Overview</h3>
      </div>
      <div className="packages-summary-grid">
        <div className="packages-summary-box packages-summary-active">
          <span className="packages-summary-label">Active Packages</span>
          <p className="packages-summary-value">{activeCount}</p>
          <span className="packages-summary-sub">₹{Number(activeValue).toLocaleString("en-IN")} value</span>
        </div>
        <div className="packages-summary-box packages-summary-expiring">
          <span className="packages-summary-label">Nearing Expiry</span>
          <p className="packages-summary-value">{nearingExpiryCount}</p>
          <span className="packages-summary-sub">Within {expiryWindowDays} days</span>
        </div>
      </div>
      {expiringSoon && expiringSoon.length > 0 && (
        <div className="packages-expiring-list">
          <span className="packages-expiring-title">Expiring Soon</span>
          <ul>
            {expiringSoon.slice(0, 4).map((p) => (
              <li className="packages-expiring-row" key={p.packageId}>
                <div>
                  <p className="packages-expiring-customer">{p.customerName}</p>
                  <span className="packages-expiring-detail">{p.packageName} ({p.sessionsRemaining} left)</span>
                </div>
                <span className="packages-expiring-days">
                  {p.daysToExpiry !== null && p.daysToExpiry !== undefined ? `${p.daysToExpiry}d left` : "—"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Colours validated for colour-blind separation and 3:1 contrast on white
// (dataviz validator: deutan ΔE 10.6, normal-vision ΔE 16.1).
const REPEAT_COLOR = "#4f46e5";
const NEW_COLOR = "#0284c7";
const NVR_WINDOWS = [7, 15, 30];

// Rounds a max value up to 1/2/2.5/5 x 10^n so axis ticks land on round numbers.
const niceCeil = (value) => {
  if (value <= 4) return 4;
  const pow = Math.pow(10, Math.floor(Math.log10(value)));
  const step = [1, 2, 2.5, 5, 10].find((m) => m * pow >= value);
  return step * pow;
};

const formatShortDate = (isoDate) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

const ChangeBadge = ({ current, previous }) => {
  if (!previous) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  const cls = pct > 0 ? "nvr-change-up" : pct < 0 ? "nvr-change-down" : "nvr-change-flat";
  return (
    <span className={`nvr-change ${cls}`} title="Change vs. the previous period of the same length">
      {pct > 0 ? "▲" : pct < 0 ? "▼" : ""} {Math.abs(pct)}%
    </span>
  );
};

// New = first-ever bill at the selected stores falls in the window; repeat =
// had a bill there before it. Summary counts are unique customers in the
// window; the chart is per day (see getNewVsRepeatCustomers in the CRM engine).
class NewVsRepeatCard extends Component {
  state = { hoverIndex: null };

  onMouseMove = (evt, plot, count) => {
    const rect = evt.currentTarget.getBoundingClientRect();
    const x = ((evt.clientX - rect.left) / rect.width) * plot.width;
    const idx = Math.round(((x - plot.left) / plot.innerWidth) * (count - 1));
    this.setState({ hoverIndex: Math.max(0, Math.min(count - 1, idx)) });
  };

  renderChart(daily) {
    const plot = { width: 340, height: 150, left: 28, right: 8, top: 10, bottom: 22 };
    plot.innerWidth = plot.width - plot.left - plot.right;
    plot.innerHeight = plot.height - plot.top - plot.bottom;

    const count = daily.length;
    const maxValue = niceCeil(Math.max(...daily.map((d) => Math.max(d.newCustomers, d.repeatCustomers)), 1));
    const xAt = (i) => plot.left + (count === 1 ? plot.innerWidth / 2 : (i / (count - 1)) * plot.innerWidth);
    const yAt = (v) => plot.top + plot.innerHeight - (v / maxValue) * plot.innerHeight;
    const path = (key) => daily.map((d, i) => `${i === 0 ? "M" : "L"}${xAt(i).toFixed(1)},${yAt(d[key]).toFixed(1)}`).join(" ");
    const ticks = [0, maxValue / 2, maxValue];
    const labelIdx = [...new Set([0, Math.floor((count - 1) / 2), count - 1])];
    const { hoverIndex } = this.state;
    const hovered = hoverIndex !== null ? daily[hoverIndex] : null;

    return (
      <div className="nvr-chart" onMouseLeave={() => this.setState({ hoverIndex: null })}>
        <svg
          viewBox={`0 0 ${plot.width} ${plot.height}`}
          className="nvr-svg"
          role="img"
          aria-label="Daily new and repeat customers"
          onMouseMove={(evt) => this.onMouseMove(evt, plot, count)}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line x1={plot.left} x2={plot.width - plot.right} y1={yAt(t)} y2={yAt(t)} className={t === 0 ? "nvr-baseline" : "nvr-grid"} />
              <text x={plot.left - 5} y={yAt(t) + 3} className="nvr-axis-label" textAnchor="end">{t}</text>
            </g>
          ))}
          {labelIdx.map((i) => (
            <text key={i} x={xAt(i)} y={plot.height - 5} className="nvr-axis-label"
              textAnchor={i === 0 ? "start" : i === count - 1 ? "end" : "middle"}>
              {formatShortDate(daily[i].date)}
            </text>
          ))}
          <path d={path("repeatCustomers")} fill="none" stroke={REPEAT_COLOR} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          <path d={path("newCustomers")} fill="none" stroke={NEW_COLOR} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {hovered && (
            <g>
              <line x1={xAt(hoverIndex)} x2={xAt(hoverIndex)} y1={plot.top} y2={plot.top + plot.innerHeight} className="nvr-crosshair" />
              <circle cx={xAt(hoverIndex)} cy={yAt(hovered.repeatCustomers)} r="4" fill={REPEAT_COLOR} stroke="#fff" strokeWidth="2" />
              <circle cx={xAt(hoverIndex)} cy={yAt(hovered.newCustomers)} r="4" fill={NEW_COLOR} stroke="#fff" strokeWidth="2" />
            </g>
          )}
          <rect x={plot.left} y={plot.top} width={plot.innerWidth} height={plot.innerHeight} fill="transparent" />
        </svg>
        {hovered && (
          <div
            className="nvr-tooltip"
            style={{
              left: `${(xAt(hoverIndex) / plot.width) * 100}%`,
              transform: hoverIndex > count / 2 ? "translateX(calc(-100% - 8px))" : "translateX(8px)",
            }}
          >
            <div className="nvr-tooltip-date">{formatShortDate(hovered.date)}</div>
            <div><span className="nvr-dot" style={{ background: REPEAT_COLOR }}></span>Repeat <strong>{hovered.repeatCustomers}</strong></div>
            <div><span className="nvr-dot" style={{ background: NEW_COLOR }}></span>New <strong>{hovered.newCustomers}</strong></div>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { data, windowDays, onWindowChange, loading } = this.props;
    const current = data ? data.current : null;
    const previous = data ? data.previous : null;
    const total = current ? current.total : 0;
    const pct = (n) => (total > 0 ? Math.round((n / total) * 100) : 0);

    return (
      <div className="dashboard-card nvr-card">
        <div className="breakdown-header">
          <h3 className="breakdown-title">New vs. Repeat Customers</h3>
          <div className="nvr-toggle" role="group" aria-label="Period">
            {NVR_WINDOWS.map((w) => (
              <button
                key={w}
                type="button"
                className={`nvr-toggle-btn ${w === windowDays ? "nvr-toggle-active" : ""}`}
                aria-pressed={w === windowDays}
                onClick={() => onWindowChange(w)}
              >
                {w}D
              </button>
            ))}
          </div>
        </div>

        {!data ? (
          <p className="nvr-empty">{loading ? "Loading…" : "Couldn't load this period."}</p>
        ) : total === 0 ? (
          <p className="nvr-empty">No customer bills in the last {windowDays} days.</p>
        ) : (
          <div className={loading ? "nvr-body nvr-loading" : "nvr-body"}>
            <div className="nvr-summary">
              <div className="nvr-stat">
                <div className="nvr-stat-value">
                  <span className="nvr-dot" style={{ background: REPEAT_COLOR }}></span>
                  {pct(current.repeatCustomers)}%
                  <span className="nvr-stat-count">({current.repeatCustomers})</span>
                  <ChangeBadge current={current.repeatCustomers} previous={previous.repeatCustomers} />
                </div>
                <span className="nvr-stat-label">Repeat customers</span>
              </div>
              <div className="nvr-stat">
                <div className="nvr-stat-value">
                  <span className="nvr-dot" style={{ background: NEW_COLOR }}></span>
                  {pct(current.newCustomers)}%
                  <span className="nvr-stat-count">({current.newCustomers})</span>
                  <ChangeBadge current={current.newCustomers} previous={previous.newCustomers} />
                </div>
                <span className="nvr-stat-label">New customers</span>
              </div>
            </div>
            {this.renderChart(data.daily)}
            <p className="nvr-footnote">Unique customers who billed in the last {windowDays} days. Change is vs. the {windowDays} days before.</p>
          </div>
        )}
      </div>
    );
  }
}

class CustomerDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      summary: null,
      segments: [],
      walletDetails: [],
      loyaltyGaps: [],
      membershipGaps: [],
      membershipPlans: [],
      milestones: [],
      packagesOverview: null,
      newVsRepeat: null,
      newVsRepeatWindow: 15,
      newVsRepeatLoading: false,
      loading: true
    };
    this.segmentOrder = [
      "Champions",
      "Loyal Customers",
      "Potential Loyalist",
      "New Promising",
      "At Risk",
      "Lost",
    ];
  }

  componentDidMount() {
    this.fetchDashboard();
  }

  componentDidUpdate(prevProps) {
    // Driven by the Reports page's shared store dropdown (DashboardCtrl) —
    // '' means "All Stores", a store key means one specific store. Also
    // refetch if the accessible-stores list itself resolves/changes after
    // mount, since "All Stores" is derived from it (see getSelectedStores).
    if (prevProps.storeID !== this.props.storeID || prevProps.stores !== this.props.stores) {
      this.fetchDashboard();
    }
  }

  // This engine has no auth of its own — nothing server-side stops a request
  // from asking for a store the caller shouldn't see. The only place "which
  // stores can this user access" is known is here, via `stores`, already
  // scoped by getUserStores for the logged-in user. So a specific store maps
  // to a one-entry list; "All Stores" must NOT become [] (that asks the
  // backend for literally every store in the registration) — it has to be
  // every store THIS user can see, i.e. `stores` minus the blank-key "All"
  // sentinel DashboardCtrl unshifts into that same list for the dropdown.
  getSelectedStores = () => {
    if (this.props.storeID) return [{ key: this.props.storeID }];
    return (this.props.stores || [])
      .filter(s => s.key)
      .map(s => ({ key: s.key }));
  };

  // Each widget's request succeeds or fails on its own: a network error,
  // non-2xx status, or non-JSON body resolves to {} for that one call
  // instead of throwing and leaving the whole dashboard empty.
  postApi = (path, extra = {}) =>
    fetch(`${API_BASE}/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        registrationId: this.props.user.user.RegistrationID,
        stores: this.getSelectedStores(),
        ...extra,
      }),
    })
      .then((res) => (res.ok ? res.json() : {}))
      .catch((err) => {
        console.error(`Dashboard API error (${path}):`, err);
        return {};
      });

  // The 7D/15D/30D toggle only refetches this one widget.
  onNewVsRepeatWindowChange = async (windowDays) => {
    if (windowDays === this.state.newVsRepeatWindow) return;
    this.setState({ newVsRepeatWindow: windowDays, newVsRepeatLoading: true });
    const data = await this.postApi("new-vs-repeat", { windowDays });
    this.setState({
      newVsRepeat: data && data.current ? data : null,
      newVsRepeatLoading: false,
    });
  };

  // Remember the CRM tab so DashboardCtrl reopens it when the user comes
  // back from the customer page.
  openCustomerPage = (state) => {
    try {
      sessionStorage.setItem("dashboardActiveTab", "CRM");
    } catch (e) {}
    this.props.history.push({
      pathname: "/home/customer",
      state: state
    });
  };

  fetchDashboard = async () => {
    try {
      this.setState({ loading: true });

      const post = this.postApi;

      const [
        summaryData,
        segmentData,
        walletData,
        loyaltyData,
        membershipData,
        membershipBreakdownData,
        milestonesData,
        packagesData,
        newVsRepeatData,
      ] = await Promise.all([
        post("summary"),
        post("segments"),
        post("wallet-summary"),
        post("wallet-membership"),
        post("membership-summary"),
        post("membership-breakdown"),
        post("milestones", { windowDays: 30, limit: 12 }),
        post("packages-overview"),
        post("new-vs-repeat", { windowDays: this.state.newVsRepeatWindow }),
      ]);

      this.setState({
        membershipCount: membershipData.customers_with_membership || 0,
        membershipGaps: loyaltyData.totalMembershipCount || [],
        loyaltyGaps: loyaltyData.totalWalletCount || [],
        walletDetails: walletData,
        summary: summaryData,
        segments: Array.isArray(segmentData.segments) ? segmentData.segments : [],
        membershipPlans: membershipBreakdownData.plans || [],
        milestones: milestonesData.milestones || [],
        packagesOverview: packagesData && packagesData.activeCount !== undefined ? packagesData : null,
        newVsRepeat: newVsRepeatData && newVsRepeatData.current ? newVsRepeatData : null,
        loading: false
      });
    } catch (error) {
      console.error("Dashboard API error:", error);
      this.setState({ loading: false });
    }
  };

  formatUpdatedDate = (dateValue) => {
    const date = new Date(Number(dateValue));
    const now = new Date();

    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    const timePart = date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    if (isToday) {
      return `Today, ${timePart}`;
    }

    const datePart = date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    return `${datePart}, ${timePart}`;
  };

  // The store picked in the dashboard filter; "All Stores" (empty key) falls
  // back to the logged-in store so the inquiry always names a store.
  getSelectedStore = () => {
    const { storeID, stores, user } = this.props;
    const selected = (stores || []).find((st) => st.key && st.key === storeID);
    return selected || (user && user.store) || {};
  };

  // Looks up the change_percent / trend_label for a segment from the
  // `summary` response, so the segment list row can show the same
  // increase/decrease indicator that used to live in the summary boxes.
  getSegmentTrend = (segmentName) => {
    const { summary } = this.state;
    const key = SEGMENT_SUMMARY_KEY_MAP[segmentName];
    if (!summary || !key || !Object.prototype.hasOwnProperty.call(summary, key) || !summary[key]) {
      return null;
    }
    const entry = summary[key];
    return {
      percent: Object.prototype.hasOwnProperty.call(entry, 'change_percent') ? entry.change_percent : 0,
      label: Object.prototype.hasOwnProperty.call(entry, 'trend_label') ? entry.trend_label : "",
    };
  };

  render() {
    const { loading, summary, segments, walletDetails, loyaltyGaps, membershipCount, membershipPlans, milestones, packagesOverview } = this.state;

    if (loading) {
      return <Loader show={loading} />;
    }

    const totalCustomers = summary && Object.prototype.hasOwnProperty.call(summary, 'totalCustomers') ? summary.totalCustomers : 0;
    const analysisDays = (summary && summary.analysis_days) ? summary.analysis_days : DEFAULT_ANALYSIS_PERIOD_DAYS;
    const analysisPeriodMonths = analysisDays / 30;
    const analysisLabel = (summary && summary.analysis_label) ? summary.analysis_label : `Last ${DEFAULT_ANALYSIS_PERIOD_DAYS} Days`;
    // const activeMembershipCount = (membershipGaps || []).reduce((sum, g) => sum + (g && g.count ? g.count : 0), 0);
    const activeMembershipCount = membershipCount

    // Total Customers' own trend isn't a separate field — it's the sum of
    // every segment's change_count (an additive absolute delta) from the
    // same `summary` response the segment rows already read their trends
    // from, not a new metric.
    const segmentSummaryKeys = Object.values(SEGMENT_SUMMARY_KEY_MAP);
    const totalChangeCount = summary
      ? segmentSummaryKeys.reduce((sum, key) => sum + ((summary[key] && summary[key].change_count) || 0), 0)
      : 0;
    const previousTotalCustomers = totalCustomers - totalChangeCount;
    const totalChangePercent = previousTotalCustomers > 0
      ? Math.round((totalChangeCount / previousTotalCustomers) * 100)
      : 0;
    const totalTrendLabel = summary
      ? segmentSummaryKeys.map(key => summary[key] && summary[key].trend_label).find(Boolean)
      : "";
    const totalCustomersTrend = totalTrendLabel ? { percent: totalChangePercent, label: totalTrendLabel } : null;

    // Avg Spend (AOV) is the segment-weighted average across the same
    // /segments data the table below renders — sum(avgAOV * customerCount)
    // / total customers — not a separately fetched figure.
    const segmentCustomerTotal = segments.reduce((sum, s) => sum + (s.customerCount || 0), 0);
    const avgSpend = segmentCustomerTotal > 0
      ? segments.reduce((sum, s) => sum + (Number(s.avgAOV) || 0) * (s.customerCount || 0), 0) / segmentCustomerTotal
      : 0;

    return (
      <div className="dashboard-container">

        <div className="dashboard-refresh-pill">
          <span className="refresh-pulse-dot"></span>
          <span className="refresh-pill-label">Refreshes once daily</span>
          {summary && summary.reportDate && (
            <>
              <span className="refresh-pill-divider">•</span>
              <span>Updated {formatLastUpdated(summary.reportDate)}</span>
            </>
          )}
        </div>

        <InquiryBanner
          user={this.props.user}
          storeID={this.props.storeID}
          storeName={this.getSelectedStore().StoreName || ""}
          contactNo={this.getSelectedStore().PhoneNo || (this.props.user.user && this.props.user.user.PhoneNo) || ""}
        />

        <div className="summary-cards-grid mt-0">
          <SummaryCard
            title="Total Customers"
            periodLabel={analysisLabel}
            value={totalCustomers}
            trend={totalCustomersTrend}
          />
          <CustomerCaptureCard data={summary && Object.prototype.hasOwnProperty.call(summary, 'customer_capture_rate') ? summary.customer_capture_rate : null} />
          <WalletLiabilityCard
            total={walletDetails && Object.prototype.hasOwnProperty.call(walletDetails, 'total_wallet_balance') ? walletDetails.total_wallet_balance : 0}
            customerCount={walletDetails && Object.prototype.hasOwnProperty.call(walletDetails, 'customers_with_wallet') ? walletDetails.customers_with_wallet : 0}
          />
          <ActiveMembershipCard
            activeCount={activeMembershipCount}
            totalCustomers={totalCustomers}
          />
          <AverageSpendCard avgSpend={avgSpend} />
        </div>

        <div className="segment-milestones-grid">
          <div className="segment-main-column">
          <div className="segment-table">
            <div className="segment-header" style={{ marginBottom: 4, paddingBottom: 4 }}>
              <div>
                <div className="segment-title">Segment Performance</div>
              </div>
            </div>

            <div className="segment-column-headers" style={{ }}>
              <div className="col-segment-name">Segment Name</div>
              <div className="col-customers">Customers</div>
              <div className="col-avg-spend">AVG Spend (AOV)</div>
              <div className="col-buying-cycle">BUYING CYCLE</div>
              <div className="col-avg-recency">AVG Recency</div>
              <div className="col-insights">Insights</div>
            </div>

            <div>
              {segments
                .sort((a, b) => {
                  const indexA = this.segmentOrder.indexOf(a.segment);
                  const indexB = this.segmentOrder.indexOf(b.segment);
                  return indexA - indexB;
                })
                .map((seg) => {
                  const gap = loyaltyGaps.find((g) => g.segment === seg.segment);
                  const loyaltyGap = gap
                    ? {
                        count: gap.count,
                        percent: (gap.count / seg.customerCount) * 100,
                      }
                    : null;

                  return (
                    <SegmentRow
                      key={seg.segment}
                      name={seg.segment}
                      avgAOV={seg.avgAOV}
                      avgRecency={seg.avgRecencyDays}
                      avgFrequencyRaw={seg.avgFrequencyRaw}
                      avgFrequencyLast60Days={seg.avgFrequencyLast60Days}
                      analysisPeriodMonths={analysisPeriodMonths}
                      health={seg.health}
                      customerCount={seg.customerCount}
                      loyaltyGap={loyaltyGap}
                      trend={this.getSegmentTrend(seg.segment)}
                      onClick={() =>
                        this.openCustomerPage({ segment: seg.segment })
                      }
                      onClickLoyalty={() =>
                        this.openCustomerPage({ segment: seg.segment, wallet: "Empty" })
                      }
                    />
                  );
                })}
            </div>
          </div>

          <div className="bottom-grid">
            <MembershipBreakdownCard plans={membershipPlans} totalCustomers={totalCustomers} />
            <PackagesOverviewCard data={packagesOverview} />
          </div>
          </div>

          <div className="segment-side-column">
            <NewVsRepeatCard
              data={this.state.newVsRepeat}
              windowDays={this.state.newVsRepeatWindow}
              loading={this.state.newVsRepeatLoading}
              onWindowChange={this.onNewVsRepeatWindowChange}
            />
            <MilestonesFeed milestones={milestones} />
          </div>
        </div>
      </div>
    );
  }
}

export default CustomerDashboard;